import React, { useState, useEffect, useRef, useMemo, memo } from "react";
import {
  Select, Button, Row, Col, Empty, Divider, Statistic,
  Card, Drawer, Space, Typography, Badge, Tooltip, Spin,
  Switch, Segmented, Progress, Avatar, Collapse, Tabs,
  InputNumber, Slider, DatePicker, AutoComplete, Tag, notification
} from "antd";
import { 
  FilterOutlined, 
  ReloadOutlined, 
  ClearOutlined,
  DollarOutlined,
  FileTextOutlined,
  UserOutlined,
  CalendarOutlined,
  RiseOutlined,
  BarChartOutlined,
  PieChartOutlined,
  TeamOutlined,
  BankOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  FullscreenOutlined,
  ExportOutlined,
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  ThunderboltOutlined,
  FireOutlined,
  StarOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  ExpandOutlined,
  CompressOutlined,
  SettingOutlined,
  HeartOutlined,
  SafetyOutlined,
  DatabaseOutlined,
  CloudOutlined,
  MobileOutlined,
  DesktopOutlined
} from "@ant-design/icons";
import { useAuth } from "../../assets/utils/useAuth";
import { usePermissions } from "../../assets/utils/usePermissions";
import { useCountersEnhanced } from "../../hooks/useCountersEnhanced";
import "./index.css";
import { myRequest } from "../../assets/utils/lib";
import CountUp from "react-countup";
import Organizations from "../orgs";
import CheckinListHook, { CheckinListAnalytics, CheckinListAnalyticsInPatioent } from "../checkins";
import { ClaimsListHook } from "../claims";
import ClaimsListHookPayment from "../claims/ClaimsListHookPayment";
import { gender, states } from "../../assets/utils/config";
import { useNavigate, useLocation } from "react-router-dom";
import Logging from "../logging";
import Loader from "../compoinents/loader";
import DashboardMasthead from "./components/DashboardMasthead";
import DashboardBody from "./components/DashboardBody";

const { Title, Text } = Typography;

export const formatter = (value) => <CountUp end={value} separator="," />;

const PlansSummary = (props) => {
  const { user } = useAuth();
  const { canStayOnModule, hasAnyPermissionWithLegacy } = usePermissions();
  const navigator = useNavigate();
  const { pathname } = useLocation();
  const [summary, setSummary] = useState({});
  const [filter, setFilter] = useState({});
  const [filterDate, setFilterDate] = useState({ start: null, end: null });
  const [loading, setLoading] = useState(false);
  const [loadWithFilter, setLoadWithFilter] = useState(false);
  const [filterReste, setresetFilter] = useState(false);
  const [showOrg, setShowOorg] = useState(false);
  const [showClaimsPaid, setShowClaimsPaid] = useState(false);
  const [showClaimsPendingPayment, setShowClaimsPendingPayment] =
    useState(false);

  // Statistically Related States
  const [showAllClaims, setShowAllClaims] = useState(false);
  const [showAllClaimsCFO, setShowAllClaimsCFO] = useState(false);
  const [showDeclinedClaims, setShowDeclinedClaims] = useState(false);
  const [showPendingClaims, setShowPendingClaims] = useState(false);
  const [showPaidClaims, setShowPaidClaims] = useState(false);
  const [showCheckind, setShowCheckind] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [packages, setPackages] = useState([]);

  const [checkTypeToShow, setCheckTypeToShow] = useState(null);

  // Get auth token for counters
  const authToken = user?.jwt || user?.token || user?.access_token || null;
  
  // Get counters for badges (PA, Claims, Enrollees)
  const { counters } = useCountersEnhanced(authToken, {
    adaptive: true,
    fastInterval: 15000,   // 15s when active (increased from 5s)
    slowInterval: 60000,   // 60s when idle (increased from 30s)
    idleTimeout: 120000,    // 2min idle = slow mode (increased from 1min)
  });
  
  // Which hero tiles / dashboard sections this user gets. All four are widget-visibility
  // flags over already-fetched summary data, not access control.
  const isAdmin = hasAnyPermissionWithLegacy(['admin.*'], [1]);
  const isClaimsStaff = hasAnyPermissionWithLegacy(
    ['claims.read', 'claims.read_all', 'claims.reports'],
    [1, 3, 4, 5],
  );
  const isPAStaff = hasAnyPermissionWithLegacy(
    ['preauth.read', 'preauth.decide', 'preauth.approve'],
    [1, 2, 3, 4],
  );
  const canSeeRevenue = hasAnyPermissionWithLegacy(
    ['reports.financial', 'payments.read_all', 'organizations.reports'],
    [1, 5],
  );
  // DashboardBody still selects the revenue tile/section with `[1, 5].includes(userRoleId)`.
  // That file is migrated separately, so keep its prop contract and feed it a
  // permission-derived stand-in rather than the real legacy role id.
  const userRoleId = canSeeRevenue ? 5 : 0;

  // Modern Dashboard Features
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('sidebarDarkMode');
    return saved ? JSON.parse(saved) : false;
  });
  
  // Advanced UI States
  const [dashboardLayout, setDashboardLayout] = useState('grid'); // 'grid', 'compact', 'expanded'
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(60); // seconds
  const [visibleWidgets, setVisibleWidgets] = useState({
    revenue: true,
    claims: true,
    users: true,
    checkins: true,
    analytics: true,
    trends: true
  });
  
  // Collapsible sections state
  const [collapsedSections, setCollapsedSections] = useState({
    revenue: false,
    claims: false,
    preauth: false,
    encounters: false,
    network: true,
    facilities: true,
  });
  const [fullscreenWidget, setFullscreenWidget] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [realTimeUpdates, setRealTimeUpdates] = useState(true);
  const [performanceMetrics, setPerformanceMetrics] = useState({
    responseTime: 0,
    dataFreshness: new Date(),
    apiHealth: 'excellent'
  });
  const [customDateRange, setCustomDateRange] = useState(null);
  const [showFilters, setShowFilters] = useState(true);

  const timerId = useRef();
  const currentRequest = useRef(false);
  const pickerRef = useRef();
  const interval = 60000;

  // Listen for theme changes from navigation
  useEffect(() => {
    const handleStorageChange = () => {
      const saved = localStorage.getItem('sidebarDarkMode');
      setDarkMode(saved ? JSON.parse(saved) : false);
    };

    // Listen for storage events (cross-tab)
    window.addEventListener('storage', handleStorageChange);
    
    // Listen for custom theme change events
    const handleThemeChange = () => {
      const saved = localStorage.getItem('sidebarDarkMode');
      setDarkMode(saved ? JSON.parse(saved) : false);
    };
    
    window.addEventListener('themeChanged', handleThemeChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('themeChanged', handleThemeChange);
    };
  }, []);

  // Apply dark mode class to body for CSS targeting
  useEffect(() => {
    const dashboardContainer = document.querySelector('.dashboard-container');
    if (dashboardContainer) {
      if (darkMode) {
        dashboardContainer.classList.add('dark-mode');
      } else {
        dashboardContainer.classList.remove('dark-mode');
      }
    }
  }, [darkMode]);

  // Modern Dashboard Utilities
  const toggleWidgetVisibility = (widgetKey) => {
    setVisibleWidgets(prev => ({
      ...prev,
      [widgetKey]: !prev[widgetKey]
    }));
  };

  const toggleFullscreen = (widgetKey) => {
    setFullscreenWidget(fullscreenWidget === widgetKey ? null : widgetKey);
  };

  const getHealthStatus = (value, thresholds = { good: 90, warning: 70 }) => {
    if (value >= thresholds.good) return { status: 'success', color: '#52c41a', icon: <CheckCircleOutlined /> };
    if (value >= thresholds.warning) return { status: 'warning', color: '#faad14', icon: <WarningOutlined /> };
    return { status: 'error', color: '#ff4d4f', icon: <WarningOutlined /> };
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  const formatPercentage = (value, total) => {
    if (!total) return '0%';
    return `${((value / total) * 100).toFixed(1)}%`;
  };

  const getTrendIcon = (current, previous) => {
    if (current > previous) return <ArrowUpOutlined style={{ color: '#52c41a' }} />;
    if (current < previous) return <ArrowDownOutlined style={{ color: '#ff4d4f' }} />;
    return <span style={{ color: '#8c8c8c' }}>—</span>;
  };

  // Enhanced performance tracking
  const trackPerformance = (startTime) => {
    const responseTime = Date.now() - startTime;
    setPerformanceMetrics(prev => ({
      ...prev,
      responseTime,
      dataFreshness: new Date(),
      apiHealth: responseTime < 1000 ? 'excellent' : responseTime < 3000 ? 'good' : 'slow'
    }));
  };

  // Toggle collapsible sections
  const toggleSection = (sectionName) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  // Responsive handling
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      
      // Auto-adjust layout for mobile
      if (mobile && dashboardLayout === 'expanded') {
        setDashboardLayout('compact');
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [dashboardLayout]);

  // Auto-refresh functionality
  useEffect(() => {
    let intervalId;
    
    if (autoRefresh && refreshInterval > 0) {
      intervalId = setInterval(() => {
        if (!loading && !currentRequest.current) {
          refreshSummary();
        }
      }, refreshInterval * 1000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [autoRefresh, refreshInterval, loading]);

  const loadPlans = () => {
    myRequest('post', 'orgPlansPublic')
      .then(data => data?.data)
      .then(data => {
        if (data?.error) {
          notification.error({ message: data?.message })
        } else {
          const wrangled = data?.data?.map(plan => (
            {
              label: plan.plan_name,
              value: plan.plan_id
            }
          ))
          setPackages(wrangled)
        }
      })
  }

  const loadOrgs = async (search) => {
    console.log('loadOrgs', search)
    return myRequest('post', 'organizations', {mode: 'mini', search})
      .then(data => data?.data)
      .then(data => {
        return data? data?.data?.map(org => {
          return {label: org.name, value: org.social_no, key: org.id}
        }) : []
      })
  }

  useEffect(() => {
    if (pathname !== "/dashboard") clearInterval(timerId);
  }, [pathname]);

  useEffect(() => {
    if (loadWithFilter && Object.keys(filter)?.length) {
      clearInterval(timerId);
      getSummary(true, filter);
    }

    return () => clearInterval(timerId.current);
  }, [loadWithFilter, filter]); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (filterReste) {
      clearInterval(timerId);
      getSummary(true);
      timerId.current = setInterval(() => {
        getSummary(false);
      }, interval);
    }
    setresetFilter(false);
    return () => clearInterval(timerId.current);
  }, [filterReste]); //eslint-disable-line react-hooks/exhaustive-deps

  const getSummary = (first = false, payload = {}) => {
    if (currentRequest.current && !first) return
    if (first) setLoading(true);
    let filtered = { ...payload };

    currentRequest.current = true
    myRequest("post", "plansSummary", filtered)
      .then((resp) => resp.data)
      .then((planTypes) => {
        if (planTypes && !planTypes?.error && !planTypes?.data?.length) {
          // console.log('Available data points:', planTypes?.data);
          setSummary(planTypes?.data);
        }
      })
      .finally(() => {
        setLoading(false);
        currentRequest.current = false;
      });
  };

  useEffect(() => {
    if (!user) {
      clearInterval(timerId.current);
      return;
    }
    if (!canStayOnModule('dashboard', [1, 2, 3, 4, 5])) {
      navigator('/dashboard');
      return;
    }
    getSummary(true);
    timerId.current = setInterval(() => {
      getSummary(false);
    }, interval);
    loadPlans();
    return () => clearInterval(timerId.current);
  }, [user]); //eslint-disable-line react-hooks/exhaustive-deps

  // Finanically Related
  const showOrgDrawer = () => setShowOorg(!!!showOrg);
  const showClaimsPaidDrawer = () => setShowClaimsPaid(!!!showClaimsPaid);
  const showAClaimPendingPaymentDrawer = () =>
    setShowClaimsPendingPayment(!!!showClaimsPendingPayment);

  // Statistically Related
  const showAllClaimDrawerCFO = () => setShowAllClaimsCFO(!!!showAllClaimsCFO);
  const showAllClaimDrawer = () => setShowAllClaims(!!!showAllClaims);
  const showDeclinedClaimDrawer = () => setShowDeclinedClaims(!!!showDeclinedClaims);
  const showPendingClaimDrawer = () => setShowPendingClaims(!!!showPendingClaims);
  const showPaidClaimDrawer = () => setShowPaidClaims(!!!showPaidClaims);
  const showCheckedClients = (type = null) => {
    setCheckTypeToShow(type);
    setShowCheckind(!!!showCheckind);
  }

  const showLogsDrawer = () => setShowLogs(!!!showLogs);

  const onChange = (date) => {
    setFilter((d) => ({ ...d, date: date }));
    setFilterDate({ start: date[0], end: date[1] });
  };

  const refreshSummary = () => {
    setLoadWithFilter(true);
    setresetFilter(false);
  };

  const resetFilter = () => {
    setFilter({ date: [null, null] });
    pickerRef.current.clearDates();
    setLoadWithFilter(false);
    setresetFilter(true);
  };

  const formatFilterDateMeta = () => {
    if (!loadWithFilter || !filterDate?.start) return null;
    const start = filterDate.start?.format?.('MMM D, YYYY') || filterDate.start;
    const end = filterDate.end?.format?.('MMM D, YYYY') || filterDate.end;
    return `${start} – ${end}`;
  };

  const dashboardActions = useMemo(() => ({
    navigate: navigator,
    showOrgDrawer,
    showClaimsPaidDrawer,
    showClaimsPendingPayment: showAClaimPendingPaymentDrawer,
    showAllClaimDrawerCFO,
    showAllClaimDrawer,
    showDeclinedClaimDrawer,
    showPendingClaimDrawer,
    showPaidClaimDrawer,
    showCheckedClients,
  }), [navigator]); // eslint-disable-line react-hooks/exhaustive-deps

  const lastUpdated = new Date(performanceMetrics.dataFreshness).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <div className={`dashboard-container ${darkMode ? 'dark-mode' : ''}`}>
      <div className="dashboard-page-stack">
        <DashboardMasthead
          isMobile={isMobile}
          loading={loading}
          showFilters={showFilters}
          onToggleFilters={() => setShowFilters((open) => !open)}
          loadWithFilter={loadWithFilter}
          filterDateLabel={formatFilterDateMeta()}
          lastUpdated={lastUpdated}
          onRefresh={() => {
            const startTime = Date.now();
            refreshSummary();
            setTimeout(() => trackPerformance(startTime), 100);
          }}
          onClearFilters={resetFilter}
          filter={filter}
          setFilter={setFilter}
          packages={packages}
          states={states}
          loadOrgs={loadOrgs}
          pickerRef={pickerRef}
          onDateChange={onChange}
          onApplyFilters={refreshSummary}
        />

        {(loading || Object.keys(summary).length > 0) ? (
          <DashboardBody
            loading={loading && !Object.keys(summary).length}
            summary={summary}
            counters={counters}
            userRoleId={userRoleId}
            isAdmin={isAdmin}
            isClaimsStaff={isClaimsStaff}
            isPAStaff={isPAStaff}
            collapsedSections={collapsedSections}
            toggleSection={toggleSection}
            actions={dashboardActions}
            filters={filter}
          />
        ) : null}

        {!loading && !Object.keys(summary).length ? (
          <div className="dash-empty">
            <Empty description="No data" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          </div>
        ) : null}
      </div>

      {/* Drawers */}
      <Drawer
        title="Organizations"
        placement="right"
        width={800}
        onClose={showOrgDrawer}
        open={showOrg}
      >
        <Organizations />
      </Drawer>

      <Drawer
        title="Claims Paid"
        placement="right"
        width={800}
        onClose={showClaimsPaidDrawer}
        open={showClaimsPaid}
      >
        <ClaimsListHookPayment />
      </Drawer>

      <Drawer
        title="Claims Pending Payment"
        placement="right"
        width={800}
        onClose={showAClaimPendingPaymentDrawer}
        open={showClaimsPendingPayment}
      >
        <ClaimsListHookPayment />
      </Drawer>

      <Drawer
        title="All Claims CFO"
        placement="right"
        width={800}
        onClose={showAllClaimDrawerCFO}
        open={showAllClaimsCFO}
      >
        <ClaimsListHook />
      </Drawer>

      <Drawer
        title="All Claims"
        placement="right"
        width={800}
        onClose={showAllClaimDrawer}
        open={showAllClaims}
      >
        <ClaimsListHook />
      </Drawer>

      <Drawer
        title="Declined Claims"
        placement="right"
        width={800}
        onClose={showDeclinedClaimDrawer}
        open={showDeclinedClaims}
      >
        <ClaimsListHook />
      </Drawer>

      <Drawer
        title="Pending Claims"
        placement="right"
        width={800}
        onClose={showPendingClaimDrawer}
        open={showPendingClaims}
      >
        <ClaimsListHook />
      </Drawer>

      <Drawer
        title="Paid Claims"
        placement="right"
        width={800}
        onClose={showPaidClaimDrawer}
        open={showPaidClaims}
      >
        <ClaimsListHook />
      </Drawer>

      <Drawer
        title="Check-ins"
        placement="right"
        width={800}
        onClose={() => setShowCheckind(false)}
        open={showCheckind}
      >
        {checkTypeToShow === 'inpatient' ? (
          <CheckinListAnalyticsInPatioent />
        ) : (
          <CheckinListAnalytics />
        )}
      </Drawer>

      <Drawer
        title="Logs"
        placement="right"
        width={800}
        onClose={showLogsDrawer}
        open={showLogs}
      >
        <Logging compact />
      </Drawer>
    </div>
  );
};

export default PlansSummary;
